import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Ticket, ScanLine } from "lucide-react";
import { useLang, L } from "@/i18n";
import { api } from "@/api";
import { HeartButton, SectionHead, EmptyState, MarqueeRibbon } from "@/components/Widgets";

export default function Events() {
  const { lang, t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const tracked = useRef(false);

  const fonte = searchParams.get("fonte") || "";
  const category = searchParams.get("categoria") || "";

  useEffect(() => {
    if (!tracked.current && fonte) {
      tracked.current = true;
      api.post("/track", { fonte, page: "eventi" }).catch(() => {});
    }
  }, [fonte]);

  useEffect(() => {
    setLoading(true);
    api.get("/items", { params: { type: "event", upcoming: true, limit: 300 } })
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(events.map((e) => e.category).filter(Boolean))], [events]);
  const filtered = category ? events.filter((e) => e.category === category) : events;

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((ev) => {
      const d = new Date(ev.start);
      const key = d.toLocaleDateString(lang, { month: "long", year: "numeric" });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });
    return [...map.entries()];
  }, [filtered, lang]);

  const fmt = (iso) => {
    const d = new Date(iso);
    return {
      day: d.toLocaleDateString(lang, { day: "numeric" }),
      dow: d.toLocaleDateString(lang, { weekday: "short" }),
      time: d.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" }),
    };
  };

  return (
    <div data-testid="events-page">
      <section className="bg-mare text-calce relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight">{t("events.title")}</h1>
            <p className="mt-4 max-w-2xl text-calce/85 text-base sm:text-lg">{t("events.sub")}</p>
            <Link to="/proponi-evento" data-testid="events-propose-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-[#d15f2d] text-white font-bold px-5 py-3 text-sm transition-colors shadow-lg">
              {t("events.propose")}
            </Link>
          </motion.div>
          {fonte && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
              data-testid="events-qr-banner"
              className="mt-6 inline-flex items-center gap-3 bg-white/10 border border-white/25 backdrop-blur rounded-2xl px-5 py-3.5">
              <ScanLine className="w-5 h-5 text-sabbia shrink-0" />
              <p className="text-sm text-sabbia font-medium">{t("events.qr")}</p>
              <span className="rounded-full bg-terracotta text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider whitespace-nowrap" data-testid="events-fonte-badge">{fonte}</span>
            </motion.div>
          )}
        </div>
        <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-turchese/20 blur-2xl" />
      </section>

      <MarqueeRibbon className="bg-terracotta text-white" items={["Live", "Aggiornato in tempo reale", "Dalla rivista al web", "Follonica Like"]} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" data-testid="events-categories">
          <button data-testid="events-cat-all" onClick={() => setSearchParams(fonte ? { fonte } : {}, { replace: true })}
            className={`rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${!category ? "bg-mare text-white" : "bg-white border border-black/10 text-grafite hover:bg-sabbia"}`}>
            {t("common.all")}
          </button>
          {categories.map((c) => (
            <button key={c} data-testid={`events-cat-${c}`}
              onClick={() => { const p = {}; if (fonte) p.fonte = fonte; if (c !== category) p.categoria = c; setSearchParams(p, { replace: true }); }}
              className={`rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${category === c ? "bg-mare text-white" : "bg-white border border-black/10 text-grafite hover:bg-sabbia"}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-20 text-center text-grafite/50">{t("common.loading")}</p>
        ) : filtered.length === 0 ? (
          <EmptyState text={t("events.empty")} />
        ) : (
          groups.map(([month, evs]) => (
            <section key={month} className="mt-12">
              <h2 className="font-heading font-bold text-2xl text-grafite capitalize mb-6 sticky top-[64px] lg:top-[108px] bg-calce/85 backdrop-blur-md py-2 z-20">{month}</h2>
              <div className="grid gap-4">
                {evs.map((ev, i) => {
                  const d = fmt(ev.start);
                  return (
                    <motion.div key={ev.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: i * 0.04 }}>
                      <Link to={`/scheda/${ev.slug}`} data-testid={`event-${ev.slug}`}
                        className="card-lift grid grid-cols-[72px_1fr] sm:grid-cols-[88px_140px_1fr_auto] gap-4 items-center bg-white rounded-3xl p-4 sm:p-5 shadow-[0_8px_30px_rgb(14,110,140,0.08)]">
                        <div className="flex flex-col items-center justify-center rounded-2xl bg-sabbia py-3">
                          <span className="text-[11px] uppercase font-bold text-terracotta">{d.dow}</span>
                          <span className="font-heading font-bold text-3xl text-grafite leading-none">{d.day}</span>
                          <span className="text-[11px] font-semibold text-grafite/50">{d.time}</span>
                        </div>
                        {ev.images?.[0] && (
                          <img src={ev.images[0]} alt="" loading="lazy" className="hidden sm:block w-full h-24 object-cover rounded-2xl" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-pineta text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5">{ev.category}</span>
                          </div>
                          <h3 className="font-heading font-semibold text-lg sm:text-xl text-grafite mt-1.5 leading-snug">{L(ev.title, lang)}</h3>
                          <p className="text-sm text-grafite/70 line-clamp-1 mt-1">{L(ev.excerpt, lang)}</p>
                          <p className="text-xs text-grafite/50 mt-1.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-turchese" /> {ev.address || ev.zone}</p>
                        </div>
                        <div className="hidden sm:flex flex-col items-end gap-2">
                          <HeartButton slug={ev.slug} size="sm" />
                          {ev.ticket_link && (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-terracotta"><Ticket className="w-3.5 h-3.5" /> {t("common.tickets")}</span>
                          )}
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
